import { Loader } from '@user-portal/wikr-components';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import React, { ComponentProps, useEffect, useRef } from 'react';
import Payment, { InitConfig, MessageType, SdkMessage } from '@solidgate/react-sdk';

import { selectCountry } from 'store/user/selectors';
import { selectCancellationCandidate } from 'store/subscriptions/selectors';
import { selectIsMerchantLoading, selectSolidMerchantData } from 'store/payment/selectors';
import { initSolidPayment, solidValidate } from 'store/payment/actions';
import { openModal } from 'store/modals/actions';

import { DEFAULT_COUNTRY, DEFAULT_CURRENCY } from 'constants/user';

import {
  cancelFlow_cancelResultClick,
  cancelFlow_cancelResultScreenLoad,
  cancelFlow_offerResultScreenLoad,
  OfferTypes,
} from 'services/analytics/trackers/cancelFlow';

import { useCancelSubscription } from 'hooks';

import { getValidateRecord } from './helpers';

import * as S from './styled';

import { ModalName } from 'types/modals';
import { PaymentMethods } from 'types/enums';

const SolidPaymentForm = () => {
  const dispatch = useDispatch();
  const solidMerchantData = useSelector(selectSolidMerchantData);
  const isMerchantLoading = useSelector(selectIsMerchantLoading);
  const cancelCandidate = useSelector(selectCancellationCandidate);
  const country = useSelector(selectCountry);
  const paymentMethod = cancelCandidate.payment_method;
  const { onCancelSubscription } = useCancelSubscription();
  const { t } = useTranslation();

  const appleContainerRef = useRef<HTMLDivElement | null>(null);
  const googleContainerRef = useRef<HTMLDivElement | null>(null);

  const handleError = () => {
    dispatch(
      openModal(ModalName.WARNING_MODAL, {
        onMainBtnClick: () => {
          cancelFlow_cancelResultClick({
            action: 'try_again',
            result: 'fail',
            subscription_id: cancelCandidate?.external_id,
          });
          onCancelSubscription();
        },
        onSupportClick: () =>
          cancelFlow_cancelResultClick({
            action: 'contact_support',
            result: 'fail',
            subscription_id: cancelCandidate?.external_id,
          }),
        title: t(''),
        subtitle: t('subscription.cancelFlow.commitment.chargeErrorModal.subtitle'),
        subscription_id: cancelCandidate?.external_id,
        withPaymentForm: true,
        analyticScreenLoadHandler() {
          if (!cancelCandidate) return;

          if (cancelCandidate.fee.fee_enabled) {
            cancelFlow_offerResultScreenLoad({
              result: 'fail',
              subscription_id: cancelCandidate.external_id,
              offer_type: OfferTypes.Fee_offer,
              fee_ammount: cancelCandidate.fee.fee_price,
              fee_result: 'fail',
            });
          }

          cancelFlow_cancelResultScreenLoad({ result: 'fail', subscription_id: cancelCandidate.external_id });
        },
      })
    );
  };

  const handleSuccess = (e: SdkMessage[MessageType.Success]) => {
    const order = {
      order_id: e.order.order_id,
      subscription_id: e.order.subscription_id ?? cancelCandidate.external_id,
      currency: e.order.currency,
      amount: e.order.amount,
    };

    const validateRecord = getValidateRecord(cancelCandidate, order);

    dispatch(solidValidate(validateRecord));
    onCancelSubscription();
  };

  const solidFormParams: ComponentProps<typeof Payment> = {
    applePayButtonParams: {
      color: 'black',
      integrationType: 'js',
      enabled: paymentMethod === PaymentMethods.apple_pay,
    },
    googlePayButtonParams: { type: 'plain', color: 'default', enabled: paymentMethod === PaymentMethods.google_pay },
    applePayContainerRef: appleContainerRef,
    googlePayContainerRef: googleContainerRef,
    formParams: { allowSubmit: true, autoFocus: false, enabled: false },
    merchantData: {
      paymentIntent: solidMerchantData?.payment_intent,
      merchant: solidMerchantData?.merchant,
      signature: solidMerchantData?.signature,
    } as InitConfig['merchantData'],
    width: '100%',
    onSuccess: handleSuccess,
    onError: handleError,
    onFail: handleError,
  };

  useEffect(() => {
    dispatch(
      initSolidPayment({
        country: country || DEFAULT_COUNTRY,
        currency: cancelCandidate.product.currency || DEFAULT_CURRENCY,
        price: cancelCandidate?.fee.fee_amount,
        productCode: cancelCandidate?.product_code,
        subscriptionId: cancelCandidate?.external_id,
      })
    );
  }, []);

  if (isMerchantLoading || !solidMerchantData?.payment_intent) {
    return (
      <S.LoaderContainer>
        <Loader size={24} color="text-secondary" />
      </S.LoaderContainer>
    );
  }

  return (
    <>
      <Payment {...solidFormParams} />
      {paymentMethod === PaymentMethods.google_pay && (
        <S.GooglePayBtn as="div" ref={googleContainerRef} data-option="google" />
      )}
      {paymentMethod === PaymentMethods.apple_pay && (
        <S.ApplePayBtn as="div" ref={appleContainerRef} data-option="apple" />
      )}
    </>
  );
};

export default SolidPaymentForm;
